import React, { useState } from 'react';
import { GoogleGenAI } from '@google/genai';

const MatrixTest = () => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [aiResult, setAiResult] = useState(null);

  const API_KEY = 'AIzaSyB26HA308zw-_6spUOgbsh2m7hegLeKqq8';

  const questions = [
    {
      id: 1,
      question: "Do you feel anxious when making important life decisions without your parents' or family's approval?",
      options: [
        { label: "Always feel anxious", value: 4 },
        { label: "Sometimes feel anxious", value: 3 },
        { label: "Rarely feel anxious", value: 2 },
        { label: "Never feel anxious", value: 1 }
      ]
    },
    {
      id: 2,
      question: "How often do you choose a 'safe' path instead of following what you truly want to do?",
      options: [
        { label: "Almost always play it safe", value: 4 },
        { label: "Often choose safety", value: 3 },
        { label: "Sometimes take risks", value: 2 },
        { label: "Usually follow my heart", value: 1 }
      ]
    },
    {
      id: 3,
      question: "How much does 'what people will say' (log kya kahenge) influence your decisions?",
      options: [
        { label: "Controls most of my choices", value: 4 },
        { label: "Influences many decisions", value: 3 },
        { label: "Consider it but don't let it control me", value: 2 },
        { label: "Don't care about others' opinions", value: 1 }
      ]
    },
    {
      id: 4,
      question: "When you think about your career or life goals, whose happiness matters more?",
      options: [
        { label: "Mostly my parents'/family's happiness", value: 4 },
        { label: "Both, but family comes first", value: 3 },
        { label: "Balanced between both", value: 2 },
        { label: "Primarily my own happiness", value: 1 }
      ]
    },
    {
      id: 5,
      question: "Have you tried something new or unconventional in the past 6 months just because YOU wanted to?",
      options: [
        { label: "No, I stick to expectations", value: 4 },
        { label: "Thought about it but didn't", value: 3 },
        { label: "Tried small things", value: 2 },
        { label: "Yes, I explore freely", value: 1 }
      ]
    }
  ];

  const handleAnswer = async (value) => {
    const newAnswers = { ...answers, [currentQuestion]: value };
    setAnswers(newAnswers);

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // All questions answered, get AI analysis
      await getAIAnalysis(newAnswers);
    }
  };

  const getAIAnalysis = async (finalAnswers) => {
    setIsAnalyzing(true);
    
    const totalScore = Object.values(finalAnswers).reduce((sum, val) => sum + val, 0);
    const maxScore = questions.length * 4;
    const percentage = (totalScore / maxScore) * 100;

    try {
      // Initialize the Google GenAI client
      const ai = new GoogleGenAI({ apiKey: API_KEY });

      const prompt = `You are analyzing a "Matrix Mindset Test" for someone in South Asia. 

Their score: ${totalScore}/${maxScore} (${percentage.toFixed(0)}%)

Score ranges:
- 65%+ = Deep in the Matrix (highly conditioned)
- 40-65% = Half Awake (aware but still afraid)
- Below 40% = Free Mind (independent thinker)

Provide a personalized result in this EXACT format:

CATEGORY: [one of: "Deep in the Matrix" OR "Half Awake" OR "Free Mind"]

MESSAGE: [2 sentences about their current state]

ADVICE:
1. [First actionable advice]
2. [Second actionable advice]
3. [Third actionable advice]

Keep it empathetic, practical, and under 120 words total. Focus on South Asian context (family pressure, society judgment, fear of failure).`;

      const response = await ai.models.generateContent({
        model: "gemini-2.5-flash",
        contents: prompt,
        generationConfig: {
          temperature: 0.8,
          maxOutputTokens: 250,
        }
      });

      parseAIResult(response.text, percentage);
    } catch (error) {
      console.error('AI Error:', error);
      manualResult(percentage);
    }

    setIsAnalyzing(false);
    setShowResult(true);
  };

  const parseAIResult = (text, percentage) => {
    const categoryMatch = text.match(/CATEGORY:\s*(.+)/i);
    const messageMatch = text.match(/MESSAGE:\s*(.+?)(?=ADVICE:|$)/is);
    const adviceMatches = [...text.matchAll(/\d+\.\s*(.+?)(?=\d+\.|$)/gs)];

    const category = categoryMatch ? categoryMatch[1].trim() : determineCategory(percentage);
    const message = messageMatch ? messageMatch[1].trim() : "You're on a journey of self-discovery.";
    const advice = adviceMatches.length > 0 
      ? adviceMatches.map(m => m[1].trim())
      : getDefaultAdvice(percentage);

    setAiResult({
      category,
      message,
      advice,
      emoji: getEmoji(category),
      color: getColor(category),
      percentage: percentage.toFixed(0)
    });
  };

  const manualResult = (percentage) => {
    const category = determineCategory(percentage);
    const advice = getDefaultAdvice(percentage);
    
    setAiResult({
      category,
      message: getMessage(category),
      advice,
      emoji: getEmoji(category),
      color: getColor(category),
      percentage: percentage.toFixed(0)
    });
  };

  const determineCategory = (percentage) => {
    if (percentage >= 65) return "Deep in the Matrix";
    if (percentage >= 40) return "Half Awake";
    return "Free Mind";
  };

  const getMessage = (category) => {
    const messages = {
      "Deep in the Matrix": "You're living mostly by external expectations. It's time to rediscover your individuality and start making choices for yourself.",
      "Half Awake": "You're aware of societal pressure but still afraid to act freely. You're on the right path - keep questioning and growing.",
      "Free Mind": "You think independently and follow your own path. Keep inspiring others to break free!"
    };
    return messages[category] || "You're on your journey.";
  };

  const getDefaultAdvice = (percentage) => {
    if (percentage >= 65) {
      return [
        "Start with small decisions - choose what YOU want for lunch, weekend plans",
        "Question one 'rule' you've always followed - ask yourself WHY",
        "Talk to someone who lives authentically - learn from their courage"
      ];
    } else if (percentage >= 40) {
      return [
        "Take one calculated risk this month - something you've wanted to try",
        "Practice saying 'no' to things you don't genuinely want to do",
        "Share one independent thought respectfully - see what happens"
      ];
    } else {
      return [
        "Share your journey with others still trapped",
        "Be a role model for independent thinking in your circle",
        "Keep growing and never stop questioning the status quo"
      ];
    }
  };

  const getEmoji = (category) => {
    const emojis = {
      "Deep in the Matrix": "🔴",
      "Half Awake": "🟡",
      "Free Mind": "🟢"
    };
    return emojis[category] || "🎯";
  };

  const getColor = (category) => {
    const colors = {
      "Deep in the Matrix": "danger",
      "Half Awake": "warning",
      "Free Mind": "accent"
    };
    return colors[category] || "primary";
  };

  const resetTest = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setShowResult(false);
    setAiResult(null);
  };

  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <section id="matrix-test" className="min-h-screen flex items-center py-20 px-4">
      <div className="container mx-auto">
        <div className="max-w-3xl mx-auto">
          {/* Section Title */}
          {!showResult && !isAnalyzing && (
            <div className="text-center mb-12 animate-fadeIn">
              <div className="text-6xl mb-6">🧩</div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-heading">
                Are You Living Inside the <span className="bg-gradient-to-r from-danger to-warning bg-clip-text text-transparent">Matrix</span>?
              </h2>
              <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-8">
                5 simple questions to discover if you're living with a free mindset or trapped in societal conditioning.
              </p>
              
              {/* Progress Bar */}
              <div className="max-w-md mx-auto">
                <div className="flex justify-between text-sm text-gray-500 mb-2">
                  <span>Question {currentQuestion + 1} of {questions.length}</span>
                  <span>{Math.round(progress)}%</span>
                </div>
                <div className="w-full bg-dark rounded-full h-2.5">
                  <div 
                    className="bg-gradient-to-r from-primary via-accent to-secondary h-2.5 rounded-full transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              </div>
            </div>
          )}

          {/* Question Card */}
          {!showResult && !isAnalyzing && (
            <div className="bg-gradient-to-br from-dark/90 to-darker/90 backdrop-blur-sm border-2 border-primary/30 rounded-3xl p-8 md:p-10 shadow-2xl animate-slideUp">
              <div className="mb-8">
                <div className="inline-block bg-primary/20 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-4">
                  Question {questions[currentQuestion].id}
                </div>
                <h3 className="text-xl md:text-2xl text-white font-semibold leading-relaxed">
                  {questions[currentQuestion].question}
                </h3>
              </div>

              <div className="space-y-3">
                {questions[currentQuestion].options.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => handleAnswer(option.value)}
                    className="w-full p-5 rounded-2xl border-2 border-primary/20 bg-dark/30 hover:border-primary/60 hover:bg-primary/10 transition-all duration-300 transform hover:scale-[1.02] text-left group"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-base md:text-lg text-gray-200 group-hover:text-white font-medium">
                        {option.label}
                      </span>
                      <svg className="w-5 h-5 text-gray-600 group-hover:text-primary transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Analyzing State */}
          {isAnalyzing && (
            <div className="text-center py-20 animate-fadeIn">
              <div className="text-7xl mb-6 animate-pulse">🔮</div>
              <h3 className="text-3xl font-bold text-white mb-4">Analyzing Your Responses...</h3>
              <p className="text-gray-400 mb-8">Getting personalized insights for you</p>
              <div className="flex justify-center gap-2">
                <div className="w-3 h-3 bg-primary rounded-full animate-bounce"></div>
                <div className="w-3 h-3 bg-accent rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                <div className="w-3 h-3 bg-secondary rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              </div>
            </div>
          )}

          {/* Result Card */}
          {showResult && aiResult && (
            <div className="animate-fadeIn">
              <div className="text-center mb-10">
                <div className="text-8xl mb-6 animate-float">{aiResult.emoji}</div>
                <div className="inline-block bg-gradient-to-r from-primary/20 to-accent/20 px-6 py-2 rounded-full mb-4">
                  <span className="text-gray-400 text-sm">Your Result</span>
                </div>
                <h2 className="text-3xl md:text-5xl font-bold text-white mb-2 font-heading">
                  {aiResult.category}
                </h2>
                <p className="text-lg text-gray-500">{aiResult.percentage}% Matrix Score</p>
              </div>

              <div className={`bg-gradient-to-br from-${aiResult.color}/20 to-transparent border-2 border-${aiResult.color}/40 rounded-3xl p-8 md:p-10 mb-8 shadow-2xl`}>
                <p className="text-lg md:text-xl text-gray-200 leading-relaxed mb-8 text-center">
                  {aiResult.message}
                </p>

                <div className="border-t-2 border-white/10 pt-8">
                  <h4 className="text-xl font-bold text-white mb-6 font-heading flex items-center gap-2">
                    <span>💡</span> What You Can Do:
                  </h4>
                  <div className="space-y-4">
                    {aiResult.advice.map((item, index) => (
                      <div key={index} className="flex items-start gap-4 bg-dark/30 p-4 rounded-xl">
                        <div className={`w-8 h-8 rounded-full bg-${aiResult.color}/30 flex items-center justify-center flex-shrink-0 mt-0.5`}>
                          <span className={`text-${aiResult.color} font-bold text-sm`}>{index + 1}</span>
                        </div>
                        <p className="text-base text-gray-300 leading-relaxed">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Quote */}
              <div className="bg-gradient-to-r from-primary/10 to-accent/10 border-l-4 border-primary rounded-r-2xl p-6 mb-8">
                <p className="text-lg text-gray-200 italic">
                  💬 "The first step to breaking the matrix is <span className="text-accent font-bold">recognizing</span> you're in it."
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={resetTest}
                  className="bg-gradient-to-r from-primary to-accent text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-2xl hover:shadow-primary/50 transition-all duration-300 transform hover:scale-105"
                >
                  Take Test Again
                </button>
                <a href="#message">
                  <button className="border-2 border-accent text-accent px-8 py-4 rounded-full font-bold text-lg hover:bg-accent hover:text-white transition-all duration-300 transform hover:scale-105 w-full sm:w-auto">
                    Read My Message
                  </button>
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default MatrixTest;